import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { fetchApi } from '@/lib/api';

// NOTE: Route path updated
export const Route = createFileRoute('/_authenticated/events/new')({
  component: NewEventPage,
});

function NewEventPage() {
  const navigate = useNavigate();
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState(''); // Store as YYYY-MM-DD string
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetchApi('/api/admin/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: eventName,
          event_date: eventDate || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Failed to create event' }));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const createdEvent = await response.json();

      // NOTE: Update navigation path
      navigate({
        to: '/events/$eventId/share',
        params: { eventId: String(createdEvent.id) },
        replace: true,
      });
    } catch (err) {
      console.error('Failed to create event:', err);
      setError(
        err instanceof Error ? err.message : 'An unknown error occurred',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" asChild>
          <Link to="/events">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Events
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">Create New Event</h1>
        <div className="w-24"></div>
      </div>

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Event Details</CardTitle>
          <CardDescription>Enter the name and date for your new event.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error Creating Event</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          <div className="space-y-2">
              <Label htmlFor="event-name">Event Name</Label>
            <Input
                id="event-name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g., John & Jane's Wedding"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
              <Label htmlFor="event-date">Event Date (Optional)</Label>
            <Input
                id="event-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating Event...' : 'Create Event'}
            </Button>
          </form>
        </CardContent>
    </Card>
    </div>
  );
}
